'use client'

import { cx } from 'classix'
import { useImageLoader } from 'media/hooks/image/useImageLoader/useImageLoader'
import Image from 'next/image'
import { ExtraColors } from 'product-components/components/ColorSelector/ColorSelectorPicker/ExtraColors/ExtraColors'
import type { ReactNode } from 'react'
import styles from './ColorSelectorPicker.module.css'

export interface ColorSelectorPickerProps {
	readonly imageAlt: string
	readonly imageSrc: string
	readonly className?: string
	readonly 'data-testid'?: string
	readonly priority?: boolean
	/** The current product color selected by the user */
	readonly isSelected?: boolean
	/** This product color is not available for sale */
	readonly outOfStock?: boolean
	/** Show '+ [N colors]' next to color box */
	readonly extraColors?: number
}

const BOX_SIZE = 14

export function ColorSelectorPicker({
	imageSrc,
	imageAlt,
	className,
	'data-testid': dataTestId,
	isSelected = false,
	priority = false,
	outOfStock = false,
	extraColors,
}: Readonly<ColorSelectorPickerProps>): ReactNode {
	const imageLoader = useImageLoader()

	return (
		<span
			data-testid={dataTestId}
			className={cx(
				styles.colorSelectorPicker,
				isSelected && styles.selected,
				outOfStock && styles.outOfStock,
				!!extraColors && styles.withExtraColors,
				className
			)}
		>
			<Image
				src={imageSrc}
				alt={imageAlt}
				width={BOX_SIZE}
				height={BOX_SIZE}
				priority={priority}
				className={cx(outOfStock && styles.outOfStockImage)}
				loader={imageLoader}
			/>
			{!!extraColors && (
				<ExtraColors dataTestId={dataTestId} extraColors={extraColors} />
			)}
		</span>
	)
}
